import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Clear";
import { CircularProgress, InputAdornment } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React from "react";
import { useSelector } from "react-redux";
import { selectTempAssignmentData } from "../../slices/tempAssignment/tempAssignmentSlice.js";
import { selectUserSession, selectWorkgroupFilters } from "../../slices/userSession/userSessionSlice.js";
import { getMultiStationAdminStationList, sortOnCaseSensitiveTexts } from "../../util/commonUtils.js";
import {
  ALL_TAED_DATE_TYPE_FILTERS,
  ALL_TAED_INCOMING_OUTGOING_FILTERS,
  ALL_TAED_STATUS_FILTERS,
  MULTI_STATION_ADMIN_ACCESS,
} from "../../util/constants.js";
import {
  DATE_TYPE_FILTER_MUST_BE_SELECTED,
  INCOMING_OUTGOING_FILTER_MUST_BE_SELECTED,
  STATION_FILTER_MUST_BE_SELECTED,
  STATUS_FILTER_MUST_BE_SELECTED,
  WORKGROUP_FILTER_MUST_BE_SELECTED,
} from "../../util/messageConstants.js";
import StarsDatePicker from "../fundamentals/StarsDatePicker.jsx";
import StarsSelectInput from "../fundamentals/StarsSelectInput.jsx";
import { selectWorkgroupData } from "../../slices/commonConfig/commonConfigSlice.js";

const useStyles = makeStyles((theme) => ({
  filterInputs: {
    width: "18vw",
    textAlign: "left",
  },
  topRow: {
    marginTop: "5px",
  },
  pageTitle: {
    fontSize: "25px",
    textAlign: "center",
    width: "300px",
    margin: "auto",
    marginTop: "25px",
    whiteSpace: "pre-line",
  },
}));

const TemporaryAssignmentFilterForm = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const workgroupFilters = useSelector(selectWorkgroupFilters);
  const tempAssignmentData = useSelector(selectTempAssignmentData);
  const workgroupData = useSelector(selectWorkgroupData);

  const SearchButton = () => {
    return (
      <IconButton
        size="small"
        className="search-icon-button"
        onMouseDown={props.onSearchSubmit.bind(this, tempAssignmentData.searchString)}
      >
        <SearchIcon />
      </IconButton>
    );
  };

  const CancelIconButton = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={props.onSearchCancel.bind(this)}>
        <CancelIcon />
      </IconButton>
    );
  };

  return (
    <Grid container className={classes.topRow} data-testid="temporary-assignment-filter-form-desktop">
      <Grid item xs={3}>
        <Typography component={"div"}>
          <Box className={classes.pageTitle}>{props.title}</Box>
        </Typography>
      </Grid>

      <Grid item xs={9}>
        <Grid container justifyContent="center" spacing={1}>
          <Grid item xs={3}>
            <Grid container>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  <StarsSelectInput
                    label="Date Type"
                    menuMessage="Filter on Date Type"
                    value={tempAssignmentData.dateTypeFilter}
                    allOptions={ALL_TAED_DATE_TYPE_FILTERS}
                    sortComparator={sortOnCaseSensitiveTexts}
                    onChange={(event) => props.onDateTypeFilterChange(event)}
                    errors={tempAssignmentData.dateTypeFilter === "" ? DATE_TYPE_FILTER_MUST_BE_SELECTED : ""}
                    className={classes.filterInputs}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  <StarsSelectInput
                    label="Incoming/Outgoing"
                    menuMessage="Filter on Incoming/Outgoing"
                    value={tempAssignmentData.incomingOutgoingFilters}
                    allOptions={ALL_TAED_INCOMING_OUTGOING_FILTERS}
                    sortComparator={sortOnCaseSensitiveTexts}
                    onChange={(event) => props.onIncomingOutgoingFilterChange(event)}
                    errors={
                      tempAssignmentData.incomingOutgoingFilters.length <= 0
                        ? INCOMING_OUTGOING_FILTER_MUST_BE_SELECTED
                        : ""
                    }
                    className={classes.filterInputs}
                    multiple={true}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={3}>
            <Grid container>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  <StarsDatePicker
                    label="Start Date"
                    date={props.startDateFilter}
                    onChange={props.onStartDateFilterChange}
                    className={classes.filterInputs}
                    dateErrors={tempAssignmentData.startDateFilterErrors}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  <StarsSelectInput
                    label="Status"
                    menuMessage="Filter on Status"
                    value={tempAssignmentData.statusFilters}
                    allOptions={ALL_TAED_STATUS_FILTERS}
                    sortComparator={sortOnCaseSensitiveTexts}
                    onChange={(event) => props.onStatusFilterChange(event)}
                    errors={tempAssignmentData.statusFilters.length <= 0 ? STATUS_FILTER_MUST_BE_SELECTED : ""}
                    className={classes.filterInputs}
                    multiple={true}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={3}>
            <Grid container>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  <StarsDatePicker
                    label="End Date"
                    date={props.endDateFilter}
                    onChange={props.onEndDateFilterChange}
                    className={classes.filterInputs}
                    dateErrors={tempAssignmentData.endDateFilterErrors}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  <StarsSelectInput
                    label="Workgroup"
                    menuMessage="Filter on Workgroup"
                    value={workgroupFilters}
                    allOptions={Object.keys(workgroupData)}
                    sortComparator={sortOnCaseSensitiveTexts}
                    onChange={(event) => props.onWorkgroupFilterChange(event)}
                    errors={workgroupFilters.length <= 0 ? WORKGROUP_FILTER_MUST_BE_SELECTED : ""}
                    className={classes.filterInputs}
                    multiple={true}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={3}>
            <Grid container>
              <Grid item xs={12}>
                {userSession.appRole === MULTI_STATION_ADMIN_ACCESS ? (
                  <Box mt={1} mb={1}>
                    <StarsSelectInput
                      label="Station"
                      menuMessage="Filter on Station"
                      value={props.stationFilter}
                      allOptions={getMultiStationAdminStationList(userSession)}
                      sortComparator={sortOnCaseSensitiveTexts}
                      onChange={(event) => props.onStationFilterChange(event)}
                      errors={props.stationFilter === "" ? STATION_FILTER_MUST_BE_SELECTED : ""}
                      className={classes.filterInputs}
                    />
                  </Box>
                ) : (
                  <Box mt={1} mb={1}>
                    <TextField
                      className="filter-input"
                      label="Station"
                      variant="outlined"
                      value={props.stationFilter}
                      disabled
                    />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  <TextField
                    className={classes.filterInputs}
                    label="Search"
                    variant="outlined"
                    value={tempAssignmentData.searchString}
                    onChange={(event) => props.onSearchFilterChange(event)}
                    onBlur={(event) => props.onSearchBlur(event)}
                    onKeyDown={(event) => props.onSearchEnterKeyPressed(event)}
                    InputProps={{
                      endAdornment:
                        tempAssignmentData.searchString !== "" ? (
                          <InputAdornment position="end">
                            {tempAssignmentData.tempAssignmentFoundByReferenceNumberSearchIsLoading ? (
                              <CircularProgress size={16} />
                            ) : (
                              ""
                            )}
                            <CancelIconButton />
                            <SearchButton />
                          </InputAdornment>
                        ) : (
                          <SearchButton />
                        ),
                    }}
                    error={!!tempAssignmentData.searchStringErrors}
                    helperText={tempAssignmentData.searchStringErrors}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

TemporaryAssignmentFilterForm.propTypes = {
  title: PropTypes.string.isRequired,
  onDateTypeFilterChange: PropTypes.func.isRequired,
  startDateFilter: PropTypes.object.isRequired,
  onStartDateFilterChange: PropTypes.func.isRequired,
  endDateFilter: PropTypes.object.isRequired,
  onEndDateFilterChange: PropTypes.func.isRequired,
  stationFilter: PropTypes.string.isRequired,
  onStationFilterChange: PropTypes.func.isRequired,
  onIncomingOutgoingFilterChange: PropTypes.func.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  onWorkgroupFilterChange: PropTypes.func.isRequired,
  onSearchFilterChange: PropTypes.func.isRequired,
  onSearchEnterKeyPressed: PropTypes.func.isRequired,
  onSearchBlur: PropTypes.func.isRequired,
  onSearchCancel: PropTypes.func.isRequired,
  onSearchSubmit: PropTypes.func.isRequired,
};
export default TemporaryAssignmentFilterForm;
